import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  CalendarIcon,
  UserIcon,
  PaperClipIcon,
  ChatBubbleBottomCenterTextIcon,
  TagIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import FormTabs from '../common/FormTabs';
import DateTimePicker from '../common/DateTimePicker';
import TimePicker from '../common/TimePicker';
import { useCustomers } from '../../hooks/useCustomers';
import { useUsers } from '../../hooks/useUsers';
import { useAuth } from '../../contexts/AuthContext';
import { Deal, DealFormData, DealType } from '../../types/deal';
import { useLanguage } from '../../contexts/LanguageContext';
import { DatePicker } from '../common/DatePicker';

// Tag input komponens
interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}

const TagInput: React.FC<TagInputProps> = ({ value, onChange, placeholder = "Adjon hozzá tag-eket..." }) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = inputValue.trim();
      if (newTag && !value.includes(newTag)) {
        onChange([...value, newTag]);
      }
      setInputValue('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter(tag => tag !== tagToRemove));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
          >
            #{tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="ml-1 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
      />
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Nyomjon Enter-t vagy vesszőt egy tag hozzáadásához
      </p>
    </div>
  );
};

// Kereshető dropdown komponens
interface SearchableDropdownProps {
  options: Array<{ id: string; name: string }>;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  error?: string;
  disabled?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  error,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // A kiválasztott érték neve
  const selectedOption = options.find(option => option.id === value);

  // Szűrt opciók - név és ID alapján is keresünk
  const filteredOptions = searchTerm.trim() === ''
    ? options
    : options.filter(option =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.id.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Külső kattintás figyelése
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Ha van kiválasztott elem, annak nevét mutatjuk a keresőben
  useEffect(() => {
    if (selectedOption && !isOpen) {
      setSearchTerm(selectedOption.name);
    }
  }, [selectedOption, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value;
    setSearchTerm(newTerm);

    // Ha törlik a keresési kifejezést, és van kiválasztott érték, töröljük azt is
    if (newTerm === '' && value !== '') {
      onChange('');
    }

    // Keresés indításakor nyissuk meg a dropdownt
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Keresőmező */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => {
            if (!disabled) {
              setSearchTerm('');
              setIsOpen(true);
            }
          }}
          disabled={disabled}
          className={`block w-full pl-10 pr-10 py-2 border ${error ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'} rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 text-sm shadow-sm ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          placeholder={selectedOption ? selectedOption.name : placeholder}
        />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <ChevronDownIcon className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown lista */}
      {isOpen && !disabled && filteredOptions.length > 0 && (
        <div className="absolute z-[9999] w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
          <div className="py-1">
            {filteredOptions.map(option => (
              <div
                key={option.id}
                className={`px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer ${option.id === value ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-200'}`}
                onClick={() => {
                  onChange(option.id);
                  setSearchTerm('');
                  setIsOpen(false);
                }}
              >
                {option.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
};

interface DealFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: DealFormData) => Promise<void>;
  initialData?: Deal;
  customerId?: string;
}

const DealForm: React.FC<DealFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  customerId
}) => {
  const { customers, loading: customersLoading } = useCustomers();
  const { users, loading: usersLoading } = useUsers();
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tags, setTags] = useState<string[]>(initialData?.tags || []);
  const [activeTab, setActiveTab] = useState<'main' | 'outcome'>('main');
  const panel = (id: 'main' | 'outcome') => (activeTab === id ? 'space-y-5' : 'hidden');
  const toYmd = (d?: Date) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '');
  const fromYmd = (v?: string) => { if (!v) return undefined; const d = new Date(v); return isNaN(d.getTime()) ? undefined : d; };

  const { register, handleSubmit, control, reset, setValue, watch, formState: { errors } } = useForm<DealFormData>({
    defaultValues: {
      type: initialData?.type || 'phone',
      customerId: initialData?.customerId || customerId || '',
      assignedUserId: initialData?.assignedUserId || '',
      date: initialData?.date ? new Date(initialData.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      description: initialData?.description || '',
      outcome: initialData?.outcome || '',
      followUpDate: initialData?.followUpDate ? new Date(initialData.followUpDate).toISOString().split('T')[0] : undefined,
      followUpTime: initialData?.followUpDate ? new Date(initialData.followUpDate).toTimeString().split(' ')[0].substring(0, 5) : '',
      subject: initialData?.subject || '',
      status: initialData?.status || 'pending',
      tags: initialData?.tags || []
    }
  });

  const dealType = watch('type');
  const selectedCustomerId = watch('customerId');

  useEffect(() => {
    if (isOpen) {
      reset({
        type: initialData?.type || 'phone',
        customerId: initialData?.customerId || customerId || '',
        assignedUserId: initialData?.assignedUserId || '',
        date: initialData?.date ? new Date(initialData.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        description: initialData?.description || '',
        outcome: initialData?.outcome || '',
        followUpDate: initialData?.followUpDate ? new Date(initialData.followUpDate).toISOString().split('T')[0] : undefined,
        followUpTime: initialData?.followUpDate ? new Date(initialData.followUpDate).toTimeString().split(' ')[0].substring(0, 5) : '',
        subject: initialData?.subject || '',
        status: initialData?.status || 'pending'
      });
      setTags(initialData?.tags || []);
    }
  }, [isOpen, initialData, customerId, reset]);

  const submitHandler = async (data: DealFormData) => {
    try {
      setIsSubmitting(true);
      // Add tags to the data
      const dataWithTags = { ...data, tags };
      await onSubmit(dataWithTags);
      onClose();
    } catch (error) {
      console.error('Error submitting deal form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCustomerName = (customer: any) => {
    if (customer.type === 'organization') {
      return customer.companyName;
    }
    return `${customer.lastName} ${customer.firstName}`;
  };

  // Formázott ügyfelek a dropdown számára
  const customerOptions = customers?.map(customer => ({
    id: customer.id,
    name: formatCustomerName(customer)
  })) || [];

  // Formázott felhasználók a dropdown számára - csak ugyanazon szervezetből
  const userOptions = users
    ?.filter(user => {
      // Super admin mindig látja az összes felhasználót
      if (currentUser?.role === 'super_admin') return true;
      
      // A bejelentkezett felhasználót mindig megjelenítjük
      if (user.id === currentUser?.id) return true;
      
      // Ha a jelenlegi felhasználónak nincs szervezete, akkor csak önmagát látja
      if (!currentUser?.organizationId) return false;
      
      // Normál felhasználók csak ugyanazon szervezet tagjait látják
      return user.organizationId === currentUser?.organizationId;
    })
    ?.map(user => ({
      id: user.id,
      name: user.displayName || user.email
    })) || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? t('deals.edit') : t('deals.new')}
      size="4xl"
      noPadding
    >
      <form onSubmit={handleSubmit(submitHandler, () => setActiveTab('main'))} noValidate className="flex flex-col min-h-full">
        <div className="px-4 sm:px-6 flex-1 pb-5">
        <div className="sticky top-0 z-10 bg-[var(--crm-surface)] pt-3 sm:pt-4">
          <FormTabs
            sticky={false}
            tabs={[
              { id: 'main', label: 'Ügylet', icon: ChatBubbleLeftRightIcon },
              { id: 'outcome', label: 'Eredmény, követés', icon: TagIcon },
            ]}
            active={activeTab}
            onChange={(id) => setActiveTab(id as 'main' | 'outcome')}
            errorTabs={Object.keys(errors).length ? ['main'] : []}
          />
        </div>
        <div className="pt-5">
        <div className={panel('main')}>
        {/* Deal Type Selection */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <ChatBubbleBottomCenterTextIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Ügylet típusa</h3>
          </div>
          <div className="p-4">

              <div className="space-y-3">
                <label className="relative flex items-center group">
                  <input
                    type="radio"
                    value="phone"
                    {...register('type')}
                    className="sr-only peer"
                  />
                  <div className="flex items-center w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 
                                peer-checked:border-primary-500 dark:peer-checked:border-primary-400 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20
                                hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200">
                    <div className={`p-2 rounded-full mr-3 ${dealType === 'phone' ? 'bg-primary-100 dark:bg-primary-900/20' : 'bg-gray-100 dark:bg-gray-700'}`}>
                      <PhoneIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 peer-checked:text-primary-600 dark:peer-checked:text-primary-400" />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-gray-700 dark:text-gray-300 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                        Telefon
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Telefon megbeszélés</div>
                    </div>
                  </div>
                </label>

                <label className="relative flex items-center group">
                  <input
                    type="radio"
                    value="email"
                    {...register('type')}
                    className="sr-only peer"
                  />
                  <div className="flex items-center w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 
                                peer-checked:border-primary-500 dark:peer-checked:border-primary-400 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20
                                hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200">
                    <div className={`p-2 rounded-full mr-3 ${dealType === 'email' ? 'bg-primary-100 dark:bg-primary-900/20' : 'bg-gray-100 dark:bg-gray-700'}`}>
                      <EnvelopeIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 peer-checked:text-primary-600 dark:peer-checked:text-primary-400" />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-gray-700 dark:text-gray-300 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                        Email
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Email levelezés</div>
                    </div>
                  </div>
                </label>

                <label className="relative flex items-center group">
                  <input
                    type="radio"
                    value="sms"
                    {...register('type')}
                    className="sr-only peer"
                  />
                  <div className="flex items-center w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 
                                peer-checked:border-primary-500 dark:peer-checked:border-primary-400 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20
                                hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200">
                    <div className={`p-2 rounded-full mr-3 ${dealType === 'sms' ? 'bg-primary-100 dark:bg-primary-900/20' : 'bg-gray-100 dark:bg-gray-700'}`}>
                      <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 peer-checked:text-primary-600 dark:peer-checked:text-primary-400" />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-gray-700 dark:text-gray-300 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                        SMS
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">SMS üzenet</div>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Information Section */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <UserIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Alapvető információk</h3>
          </div>
          <div className="p-4">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Customer Selection - Only show if customerId is not provided */}
                {!customerId && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ügyfél *
                    </label>
                    <Controller
                      name="customerId"
                      control={control}
                      rules={{ required: 'Az ügyfél kiválasztása kötelező' }}
                      render={({ field }) => (
                        <SearchableDropdown
                          options={customerOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Ügyfél keresése..."
                          error={errors.customerId?.message}
                        />
                      )}
                    />
                  </div>
                )}

                {/* Assigned User Selection */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('deals.fields.assignedUser')}
                  </label>
                  {usersLoading && (
                    <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Felhasználók betöltése...
                      </p>
                    </div>
                  )}
                  {!usersLoading && userOptions.length === 0 && currentUser?.role !== 'super_admin' && !currentUser?.organizationId && (
                    <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        Nincs elérhető felhasználó - szervezethez rendelés szükséges
                      </p>
                    </div>
                  )}
                  <Controller
                    name="assignedUserId"
                    control={control}
                    render={({ field }) => (
                      <SearchableDropdown
                        options={[
                          { id: '', name: t('deals.fields.noAssignedUser') },
                          ...userOptions
                        ]}
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder={usersLoading ? "Felhasználók betöltése..." : "Felhasználó kiválasztása..."}
                        error={errors.assignedUserId?.message}
                        disabled={usersLoading}
                      />
                    )}
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Dátum *
                  </label>
                  <input type="hidden" {...register('date', { required: 'A dátum megadása kötelező' })} />
                  <DateTimePicker showTime={false} value={fromYmd(watch('date'))} onChange={(d) => setValue('date', toYmd(d), { shouldValidate: true })} placeholder="Válassz dátumot" />
                  {errors.date && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors.date.message}</p>}
                </div>

                {/* Subject - Only for email */}
                {dealType === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tárgy *
                    </label>
                    <input
                      type="text"
                      {...register('subject', { 
                        required: dealType === 'email' ? 'A tárgy megadása kötelező email típusnál' : false 
                      })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                      placeholder="Email tárgy..."
                    />
                    {errors.subject && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors.subject.message}</p>}
                  </div>
                )}

                {/* Duration - Only for phone */}
                {dealType === 'phone' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Időtartam (perc)
                    </label>
                    <input
                      type="number"
                      min="1"
                      {...register('duration', { 
                        valueAsNumber: true,
                        min: { value: 1, message: 'Az időtartam legalább 1 perc kell legyen' }
                      })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                      placeholder="Hívás időtartama percben..."
                    />
                    {errors.duration && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors.duration.message}</p>}
                  </div>
                )}

                {/* Phone Number - Only for SMS */}
                {dealType === 'sms' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Telefonszám *
                    </label>
                    <input
                      type="tel"
                      {...register('subject', { 
                        required: dealType === 'sms' ? 'A telefonszám megadása kötelező SMS típusnál' : false 
                      })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                      placeholder="+36 30 123 4567"
                    />
                    {errors.subject && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors.subject.message}</p>}
                  </div>
                )}

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Leírás *
                  </label>
                  <textarea
                    rows={4}
                    {...register('description', { required: 'A leírás megadása kötelező' })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                    placeholder="Részletesen írja le az ügylet tartalmát..."
                  />
                  {errors.description && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors.description.message}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>
        <div className={panel('outcome')}>
        {/* Outcome and Status Section */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <TagIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Eredmény és állapot</h3>
          </div>
          <div className="p-4">

              <div className="space-y-4">
                {/* Outcome - Full width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Eredmény
                  </label>
                  <textarea
                    rows={3}
                    {...register('outcome')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                    placeholder="Az ügylet eredménye, következő lépések..."
                  />
                </div>

                {/* Status and Follow Up Date - Two columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Állapot
                    </label>
                    <select
                      {...register('status')}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                    >
                      <option value="pending">Folyamatban</option>
                      <option value="completed">Befejezve</option>
                      <option value="rejected">Elutasítva</option>
                    </select>
                  </div>

                  {/* Follow Up Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Következő találkozó
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <DateTimePicker showTime={false} value={fromYmd(watch('followUpDate'))} onChange={(d) => setValue('followUpDate', toYmd(d))} placeholder="Dátum" />
                      <TimePicker value={watch('followUpTime') || ''} onChange={(v) => setValue('followUpTime', v)} />
                    </div>
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tag-ek
                    </label>
                    <TagInput 
                      value={tags}
                      onChange={setTags}
                      placeholder="Adjon hozzá tag-eket a könnyebb kereséshez..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>
        </div>
        </div>
        {/* Footer Buttons */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 px-4 sm:px-6 py-3 border-t border-[var(--crm-border)] bg-[var(--crm-surface)]">
          <button type="button" onClick={onClose} className="crm-btn crm-btn-secondary" disabled={isSubmitting}>
            Mégse
          </button>
          <button type="submit" className="crm-btn crm-btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Mentés...' : (initialData ? 'Módosítás' : 'Létrehozás')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DealForm; 